import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { Plus, Edit2, Trash2, UserCog, Loader2, Shield, User as UserIcon } from 'lucide-react'
import { authApi } from '@/lib/api'

interface UserAccount {
  id: number
  username: string
  display_name: string
  role: 'admin' | 'user'
  phone: string
  email: string
  created_at: string
  is_online: number
}

export function UserManagePage() {
  const { t } = useTranslation()
  const [users, setUsers] = useState<UserAccount[]>([])
  const [loading, setLoading] = useState(false)
  const [showDialog, setShowDialog] = useState(false)
  const [editingUser, setEditingUser] = useState<UserAccount | null>(null)
  const [currentUser, setCurrentUser] = useState<UserAccount | null>(null)
  const [form, setForm] = useState({
    username: '',
    password: '',
    display_name: '',
    role: 'user' as 'admin' | 'user',
    phone: '',
    email: '',
  })

  const loadCurrentUser = useCallback(async () => {
    try {
      const res: any = await authApi.getCurrentUser()
      const user = res?.data || res
      if (user) {
        setCurrentUser(user)
        sessionStorage.setItem('userName', user.display_name || user.username || 'Admin')
        sessionStorage.setItem('userRole', user.role || 'user')
      }
    } catch (err) {
      // fallback to sessionStorage
      const stored = sessionStorage.getItem('userName')
      if (stored) setCurrentUser({ id: 0, username: stored, display_name: stored, role: 'admin', phone: '', email: '', created_at: '', is_online: 1 })
    }
  }, [])

  const loadUsers = useCallback(async () => {
    setLoading(true)
    try {
      const res: any = await authApi.listUsers()
      const list = res?.data || res || []
      setUsers(Array.isArray(list) ? list : [])
    } catch (err) {
      console.error('Failed to load users:', err)
      setUsers([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadCurrentUser()
    loadUsers()
  }, [loadCurrentUser, loadUsers])

  const handleSave = async () => {
    if (!form.username.trim() || (!editingUser && !form.password.trim())) return
    try {
      if (editingUser) {
        await authApi.updateUser(editingUser.id, {
          display_name: form.display_name,
          role: form.role,
          phone: form.phone,
          email: form.email,
          ...(form.password ? { password: form.password } : {}),
        })
      } else {
        await authApi.createUser(form)
      }
      setShowDialog(false)
      setForm({ username: '', password: '', display_name: '', role: 'user', phone: '', email: '' })
      setEditingUser(null)
      loadUsers()
    } catch (err) {
      console.error('Failed to save user:', err)
      alert('保存失败')
    }
  }

  const handleDelete = async (user: UserAccount) => {
    if (user.id === currentUser?.id) {
      alert('不能删除当前登录用户')
      return
    }
    if (!confirm(`确定删除用户 "${user.display_name || user.username}" 吗？`)) return
    try {
      await authApi.deleteUser(user.id)
      loadUsers()
    } catch (err) {
      console.error('Failed to delete user:', err)
      alert('删除失败')
    }
  }

  const handleEdit = (user: UserAccount) => {
    setEditingUser(user)
    setForm({
      username: user.username,
      password: '',
      display_name: user.display_name,
      role: user.role,
      phone: user.phone || '',
      email: user.email || '',
    })
    setShowDialog(true)
  }

  const handleAdd = () => {
    setEditingUser(null)
    setForm({ username: '', password: '', display_name: '', role: 'user', phone: '', email: '' })
    setShowDialog(true)
  }

  const isAdmin = currentUser?.role === 'admin'

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <UserCog className="h-5 w-5" />
          {t('settings.userManage') || '用户管理'}
        </h2>
        {isAdmin && (
          <Button onClick={handleAdd}>
            <Plus className="mr-2 h-4 w-4" />
            {t('settings.addUser') || '添加用户'}
          </Button>
        )}
      </div>

      {!isAdmin && (
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">
              {t('settings.noPermission') || '仅管理员可管理用户'}
            </p>
          </CardContent>
        </Card>
      )}

      {isAdmin && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('settings.username') || '用户名'}</TableHead>
                  <TableHead>{t('settings.displayName') || '显示名称'}</TableHead>
                  <TableHead>{t('settings.role') || '角色'}</TableHead>
                  <TableHead>{t('settings.phone') || '手机'}</TableHead>
                  <TableHead>{t('settings.email') || '邮箱'}</TableHead>
                  <TableHead>{t('settings.createdAt') || '创建时间'}</TableHead>
                  <TableHead className="w-[150px]">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      {t('common.noData')}
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {user.role === 'admin' ? <Shield className="h-4 w-4 text-primary" /> : <UserIcon className="h-4 w-4 text-muted-foreground" />}
                          {user.username}
                          {user.id === currentUser?.id && (
                            <Badge variant="secondary" className="text-xs">{t('settings.you') || '你'}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{user.display_name}</TableCell>
                      <TableCell>
                        <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                          {user.role === 'admin' ? (t('settings.admin') || '管理员') : (t('settings.normalUser') || '普通用户')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{user.phone || '-'}</TableCell>
                      <TableCell className="text-muted-foreground">{user.email || '-'}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{user.created_at}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(user)} title={t('common.edit')}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          {user.id !== currentUser?.id && (
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(user)} title={t('common.delete')}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* User Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingUser ? (t('settings.editUser') || '编辑用户') : (t('settings.addUser') || '添加用户')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('settings.username') || '用户名'} {!editingUser && '*'}</Label>
              <Input
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                disabled={!!editingUser}
                placeholder={t('settings.usernamePlaceholder') || '输入用户名'}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {t('settings.password') || '密码'} {!editingUser && '*'}
                {editingUser && <span className="text-xs text-muted-foreground ml-2">（留空不修改）</span>}
              </Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={t('settings.passwordPlaceholder') || '输入密码'}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('settings.displayName') || '显示名称'}</Label>
              <Input
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                placeholder={t('settings.displayNamePlaceholder') || '输入显示名称'}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('settings.role') || '角色'}</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value as 'admin' | 'user' })}
              >
                <option value="admin">{t('settings.admin') || '管理员'}</option>
                <option value="user">{t('settings.normalUser') || '普通用户'}</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('settings.phone') || '手机'}</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="13800000000"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('settings.email') || '邮箱'}</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="user@example.com"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSave} disabled={!form.username.trim() || (!editingUser && !form.password.trim())}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
